import React,  {useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Building2,
  Star,
  Users,
  BarChart3,
  DollarSign,
  Briefcase,
  TrendingUp,
  Package,
  Menu,
  X,
  Coffee,
  Zap,
  ArrowRight,
  CheckCircle,
  Award
} from 'lucide-react';
import Footer from '../components/Footer';
import { Header } from '../components/Header';

// Feature Card Component
const FeatureCard = ({ icon, title, description, delay }) => {
  return (
    <motion.div 
      className="bg-slate-900 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay }}
    >
      <div className="mb-4">
        {icon}
      </div>
      <h3 className="text-xl font-bold mb-2">{title}</h3>
      <p className="text-gray-400">{description}</p>
    </motion.div>
  );
};

// Gameplay List Item Component
const GameplayListItem = ({ text, delay }) => {
  return (
    <motion.li 
      className="flex items-start"
      initial={{ opacity: 0, x: -20 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay }}
    >
      <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
      <span>{text}</span>
    </motion.li>
  );
};

// Testimonial Card Component
const TestimonialCard = ({ quote, author, rating, delay }) => {
  return (
    <motion.div 
      className="bg-slate-900 rounded-xl p-6 shadow-lg"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay }}
    >
      <div className="mb-4 flex">
        {[...Array(rating)].map((_, i) => (
          <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
        ))}
        {[...Array(5-rating)].map((_, i) => (
          <Star key={i+rating} className="w-5 h-5 text-gray-500" />
        ))}
      </div>
      <p className="text-gray-300 mb-4 italic">"{quote}"</p>
      <p className="text-blue-400 font-medium">{author}</p>
    </motion.div>
  );
};

// Video Section Component with YouTube Embed
const VideoSection = () => {
  return (
    <section id="video" className="py-20 bg-slate-900">
      <div className="container mx-auto px-4">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">See Gameplay in Action</h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Watch how HotelHero brings hotel management to life with stunning visuals and engaging gameplay.
          </p>
        </motion.div>

        <motion.div
          className="relative max-w-5xl mx-auto rounded-xl overflow-hidden shadow-2xl"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="relative pb-[56.25%] h-0">
            <iframe 
              className="absolute top-0 left-0 w-full h-full"
              src="https://www.youtube.com/embed/Ly5LbUw19S4?si=j7vI1VsO5QUeZ69r?rel=0"
              title="HotelHero Gameplay"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </motion.div>

        
        <motion.div 
          className="mt-10 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Link to="/register">
            <motion.button 
              className="px-8 py-3 bg-blue-600 rounded-lg font-medium text-lg flex items-center justify-center mx-auto hover:bg-blue-700 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Experience It Yourself <ArrowRight className="ml-2 h-5 w-5" />
            </motion.button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

const LandingPage = () => {

  return (
    <div className="bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 min-h-[100dvh] text-white">
      {/* Hero Section */}
      <div className="container mx-auto px-4 pt-8 pb-20">
    <Header />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.h2 
              className="text-4xl md:text-5xl font-bold mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
            >
              Build Your Empire, <br />
              <span className="text-blue-400">Crush</span> Your Rivals
            </motion.h2>
            <motion.p 
              className="text-lg text-gray-300 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              Experience the thrill of hotel management in this addictive tycoon game. 
              Build luxurious rooms, hire talented staff, and outmaneuver your competitors
              to become the ultimate hospitality mogul.
            </motion.p>
            <motion.div 
              className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
            >
              <Link to="/register">
                <motion.button 
                  className="px-8 py-3 bg-blue-600 rounded-lg font-medium text-lg flex items-center justify-center hover:bg-blue-700 transition-colors w-full sm:w-auto"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Play Now <ArrowRight className="ml-2 h-5 w-5" />
                </motion.button>
              </Link>
              <Link to="/login">
                <motion.button 
                  className="px-8 py-3 bg-transparent border border-white/30 rounded-lg font-medium text-lg flex items-center justify-center hover:bg-white/10 transition-colors w-full sm:w-auto"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Login
                </motion.button>
              </Link>
            </motion.div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative hidden lg:block"
          >
            {/* Hotel skyline illustration */}
            <div className="relative h-96 w-full">
              {/* Main hotel tower */}
              <motion.div 
                className="absolute left-1/2 transform -translate-x-1/2 bottom-0 w-40 h-80 bg-gradient-to-b from-blue-800 to-blue-900 rounded-t-lg shadow-xl"
                initial={{ height: 0 }}
                animate={{ height: 320 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                {/* Windows */}
                <div className="grid grid-cols-4 gap-2 p-4 h-full">
                  {[...Array(24)].map((_, i) => (
                    <motion.div 
                      key={i} 
                      className="bg-yellow-100/30 rounded-sm"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.8 + (i * 0.02) }}
                    />
                  ))}
                </div>
              </motion.div>
              
              {/* Left side building */}
              <motion.div 
                className="absolute left-1/4 bottom-0 w-32 h-64 bg-gradient-to-b from-slate-700 to-slate-800 rounded-t-lg shadow-lg"
                initial={{ height: 0 }}
                animate={{ height: 256 }}
                transition={{ duration: 0.7, delay: 0.7 }}
              >
                <div className="grid grid-cols-3 gap-2 p-3 h-full">
                  {[...Array(15)].map((_, i) => (
                    <motion.div 
                      key={i} 
                      className="bg-yellow-100/20 rounded-sm"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.9 + (i * 0.03) }}
                    />
                  ))}
                </div>
              </motion.div>
              
              {/* Right side building */}
              <motion.div 
                className="absolute right-1/4 bottom-0 w-24 h-48 bg-gradient-to-b from-slate-700 to-slate-800 rounded-t-lg shadow-lg"
                initial={{ height: 0 }}
                animate={{ height: 192 }}
                transition={{ duration: 0.6, delay: 0.8 }}
              >
                <div className="grid grid-cols-3 gap-1 p-2 h-full">
                  {[...Array(12)].map((_, i) => (
                    <motion.div 
                      key={i} 
                      className="bg-yellow-100/20 rounded-sm"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 1 + (i * 0.03) }}
                    />
                  ))}
                </div>
              </motion.div>
              
              {/* Stars in the sky */}
              {[...Array(20)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-1 h-1 bg-white rounded-full"
                  style={{
                    top: `${Math.random() * 40}%`,
                    left: `${Math.random() * 100}%`,
                  }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: [0.2, 1, 0.2] }}
                  transition={{
                    repeat: Infinity,
                    duration: 1 + Math.random() * 2,
                    delay: Math.random() * 2,
                  }}
                />
              ))}
            </div>
          </motion.div>
        </div>
        
        {/* Stats */}
        <div className="mt-24 py-8 px-6 bg-slate-800/50 rounded-xl backdrop-blur-sm">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <motion.div 
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
            >
              <h3 className="text-4xl font-bold text-blue-400">50+</h3>
              <p className="text-gray-300 mt-2">Upgrade Options</p>
            </motion.div>
            <motion.div 
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 }}
            >
              <h3 className="text-4xl font-bold text-blue-400">8</h3>
              <p className="text-gray-300 mt-2">Room Types</p>
            </motion.div>
            <motion.div 
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 }}
            >
              <h3 className="text-4xl font-bold text-blue-400">12</h3>
              <p className="text-gray-300 mt-2">Facility Options</p>
            </motion.div>
            <motion.div 
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1 }}
            >
              <h3 className="text-4xl font-bold text-blue-400">∞</h3>
              <p className="text-gray-300 mt-2">Strategic Possibilities</p>
            </motion.div>
          </div>
        </div>
      </div>

      <section id="features" className="bg-slate-800 py-20">
        <div className="container mx-auto px-4">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Game Features</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Dive into a rich hotel management experience with a variety of strategic elements to master.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <FeatureCard 
              icon={<Building2 className="w-10 h-10 text-blue-400" />}
              title="Room Management"
              description="Design and upgrade different types of rooms from Standard to luxurious Penthouses."
              delay={0.2}
            />
            <FeatureCard 
              icon={<Users className="w-10 h-10 text-blue-400" />}
              title="Staff Hiring"
              description="Recruit and train staff with unique skills to improve your hotel's services."
              delay={0.3}
            />
            <FeatureCard 
              icon={<Coffee className="w-10 h-10 text-blue-400" />}
              title="Facilities"
              description="Build restaurants, bars, spas, gyms, and more to attract diverse guests."
              delay={0.4}
            />
            <FeatureCard 
              icon={<TrendingUp className="w-10 h-10 text-blue-400" />}
              title="Marketing Campaigns"
              description="Launch strategic marketing campaigns to boost bookings and reputation."
              delay={0.5}
            />
            <FeatureCard 
              icon={<BarChart3 className="w-10 h-10 text-blue-400" />}
              title="Market Competition"
              description="Monitor and outperform competing hotels to gain the largest market share."
              delay={0.6}
            />
            <FeatureCard 
              icon={<DollarSign className="w-10 h-10 text-blue-400" />}
              title="Financial Management"
              description="Balance expenses and revenue to maximize profits and avoid bankruptcy."
              delay={0.7}
            />
          </div>
        </div>
      </section>

      <section id="gameplay" className="py-20">
        <div className="container mx-auto px-4">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Gameplay Experience</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Experience the excitement of running your own virtual hotel empire.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h3 className="text-2xl font-bold mb-4">Strategic Decision Making</h3>
              <ul className="space-y-4">
                <GameplayListItem 
                  text="Set optimal room prices based on quality, competition, and demand"
                  delay={0.2}
                />
                <GameplayListItem 
                  text="Decide which facilities to build first to maximize return on investment"
                  delay={0.3}
                />
                <GameplayListItem 
                  text="Choose between hiring more staff or improving existing staff skills"
                  delay={0.4}
                />
                <GameplayListItem 
                  text="Manage marketing campaigns to target specific customer segments"
                  delay={0.5}
                />
              </ul>
            </motion.div>
            
            <motion.div
              className="relative rounded-xl overflow-hidden shadow-2xl"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <div className="aspect-w-16 aspect-h-9 bg-slate-700 flex items-center justify-center">
                <div className="w-full h-full bg-gradient-to-br from-blue-900 to-blue-800 p-6 flex flex-col">
                  <div className="bg-slate-800 rounded-t-lg p-4 flex justify-between items-center mb-4 shadow-md">
                    <div className="flex items-center">
                      <div className="w-6 h-6 bg-blue-600 rounded-md mr-2"></div>
                      <span className="text-lg font-bold">Hotel Dashboard</span>
                    </div>
                    <div className="flex space-x-2">
                      <div className="flex items-center bg-blue-900 bg-opacity-30 px-2 py-1 rounded-md">
                        <DollarSign className="w-4 h-4 text-green-400 mr-1" />
                        <span className="text-sm">$24,350</span>
                      </div>
                      <div className="flex items-center bg-blue-900 bg-opacity-30 px-2 py-1 rounded-md">
                        <Award className="w-4 h-4 text-yellow-400 mr-1" />
                        <span className="text-sm">4.2 ★</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="bg-slate-800 p-3 rounded-lg shadow-md">
                      <h4 className="text-sm text-blue-400 mb-1">Occupancy</h4>
                      <p className="text-xl font-bold">87%</p>
                      <motion.div 
                        className="w-full h-1.5 bg-slate-700 rounded-full mt-2 overflow-hidden"
                        initial={{ width: 0 }}
                        whileInView={{ width: "100%" }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.6 }}
                      >
                        <motion.div 
                          className="h-full bg-blue-500 rounded-full"
                          initial={{ width: 0 }}
                          whileInView={{ width: "87%" }}
                          viewport={{ once: true }}
                          transition={{ duration: 1, delay: 0.8 }}
                        />
                      </motion.div>
                    </div>
                    <div className="bg-slate-800 p-3 rounded-lg shadow-md">
                      <h4 className="text-sm text-blue-400 mb-1">Revenue</h4>
                      <p className="text-xl font-bold">$3.2K</p>
                      <div className="text-xs text-green-400 flex items-center mt-2">
                        <TrendingUp className="w-3 h-3 mr-1" /> +12%
                      </div>
                    </div>
                    <div className="bg-slate-800 p-3 rounded-lg shadow-md">
                      <h4 className="text-sm text-blue-400 mb-1">Market Share</h4>
                      <p className="text-xl font-bold">42%</p>
                      <motion.div 
                        className="w-full h-1.5 bg-slate-700 rounded-full mt-2 overflow-hidden"
                        initial={{ width: 0 }}
                        whileInView={{ width: "100%" }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.7 }}
                      >
                        <motion.div 
                          className="h-full bg-purple-500 rounded-full"
                          initial={{ width: 0 }}
                          whileInView={{ width: "42%" }}
                          viewport={{ once: true }}
                          transition={{ duration: 1, delay: 0.9 }}
                        />
                      </motion.div>
                    </div>
                  </div>
                  
                  <div className="bg-slate-800 flex-grow rounded-lg shadow-md p-4 flex flex-col">
                    <h4 className="text-sm text-blue-400 mb-3">Recent Guest Activity</h4>
                    <div className="space-y-2 flex-grow">
                      <div className="flex justify-between items-center border-b border-slate-700 pb-2">
                        <span className="text-sm">Taylor Smith</span>
                        <div className="flex items-center">
                          <span className="text-xs text-gray-300 mr-2">Room 204</span>
                          <div className="flex">
                            {[...Array(5)].map((_, i) => (
                              <Star key={i} className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-between items-center border-b border-slate-700 pb-2">
                        <span className="text-sm">Jamie Lee</span>
                        <div className="flex items-center">
                          <span className="text-xs text-gray-300 mr-2">Room 118</span>
                          <div className="flex">
                            {[...Array(4)].map((_, i) => (
                              <Star key={i} className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                            ))}
                            <Star className="w-3 h-3 text-gray-500" />
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Alex Johnson</span>
                        <div className="flex items-center">
                          <span className="text-xs text-gray-300 mr-2">Room 301</span>
                          <div className="flex">
                            {[...Array(4)].map((_, i) => (
                              <Star key={i} className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                            ))}
                            <Star className="w-3 h-3 text-gray-500" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              className="order-2 lg:order-1 relative rounded-xl overflow-hidden shadow-2xl"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <div className="aspect-w-16 aspect-h-9 bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center p-8">
                <div className="w-full h-full relative">
                  <motion.div 
                    className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-60 h-60 rounded-full"
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1 }}
                  >
                    <div className="w-full h-full rounded-full border-4 border-blue-500/30 border-dashed animate-spin-slow absolute"></div>
                    <div className="w-full h-full rounded-full border-4 border-blue-400/20 absolute" style={{ transform: 'scale(0.8)' }}></div>
                    <div className="w-full h-full rounded-full border-4 border-blue-300/10 absolute" style={{ transform: 'scale(0.6)' }}></div>
                  </motion.div>
                  
                  {/* Competition Icons */}
                  <motion.div
                    className="absolute bg-red-600 p-3 rounded-full shadow-lg"
                    style={{ top: '15%', left: '15%' }}
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.4, duration: 0.5 }}
                  >
                    <Building2 className="w-6 h-6" />
                  </motion.div>
                  
                  <motion.div
                    className="absolute bg-yellow-600 p-3 rounded-full shadow-lg"
                    style={{ top: '65%', left: '25%' }}
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.6, duration: 0.5 }}
                  >
                    <Building2 className="w-6 h-6" />
                  </motion.div>
                  
                  <motion.div
                    className="absolute bg-green-600 p-3 rounded-full shadow-lg"
                    style={{ top: '25%', right: '20%' }}
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.8, duration: 0.5 }}
                  >
                    <Building2 className="w-6 h-6" />
                  </motion.div>
                  
                  <motion.div
                    className="absolute bg-blue-600 p-6 rounded-full shadow-lg"
                    style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 1.0, duration: 0.5 }}
                  >
                    <Building2 className="w-10 h-10" />
                    <motion.div
                      className="absolute top-0 right-0 bg-green-500 rounded-full w-6 h-6 flex items-center justify-center border-2 border-slate-900"
                      initial={{ scale: 0 }}
                      whileInView={{ scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: 1.2, duration: 0.3 }}
                    >
                      <span className="text-xs font-bold">YOU</span>
                    </motion.div>
                  </motion.div>
                  
                  {/* Market Share Labels */}
                  <motion.div
                    className="absolute text-xs font-medium bg-slate-700/80 backdrop-blur-sm py-1 px-2 rounded"
                    style={{ bottom: '15%', right: '30%' }}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 1.4, duration: 0.5 }}
                  >
                    Your Market Share: 42%
                  </motion.div>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              className="order-1 lg:order-2"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h3 className="text-2xl font-bold mb-4">Dynamic Market Competition</h3>
              <ul className="space-y-4">
                <GameplayListItem 
                  text="Face off against AI-driven competitor hotels that adapt their strategies"
                  delay={0.6}
                />
                <GameplayListItem 
                  text="React to market trends, seasonal changes, and random events"
                  delay={0.7}
                />
                <GameplayListItem 
                  text="Monitor market share and adjust your pricing to stay competitive"
                  delay={0.8}
                />
                <GameplayListItem 
                  text="Execute competitive marketing campaigns to steal guests from rivals"
                  delay={0.9}
                />
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {VideoSection()}

      <section id="testimonials" className="bg-slate-800 py-20">
        <div className="container mx-auto px-4">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Player Testimonials</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              See what our players are saying about HotelHero.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <TestimonialCard 
              quote="I've been playing HotelHero for weeks and still find new strategies to try. The market competition makes every decision feel important."
              author="Michael T."
              rating={5}
              delay={0.2}
            />
            <TestimonialCard 
              quote="The balance between expanding my hotel and managing finances provides a perfect challenge. I love watching my market share grow!"
              author="Sarah L."
              rating={5}
              delay={0.4}
            />
            <TestimonialCard 
              quote="Strategic depth with accessible gameplay. I especially enjoy the random events that keep you on your toes."
              author="David W."
              rating={4}
              delay={0.6}
            />
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="container mx-auto px-4">
          <motion.div 
            className="max-w-4xl mx-auto bg-gradient-to-r from-blue-900 to-blue-800 rounded-2xl p-8 md:p-16 shadow-2xl text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <motion.h2 
              className="text-3xl md:text-4xl font-bold mb-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Ready to Build Your Hotel Empire?
            </motion.h2>
            <motion.p 
              className="text-xl text-gray-300 mb-8"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              Join thousands of players competing for hospitality dominance.
              Your first hotel is just a click away!
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <Link to="/register">
                <motion.button 
                  className="px-8 py-4 bg-orange-500 rounded-lg font-medium text-xl flex items-center justify-center mx-auto hover:bg-orange-600 transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Start Playing Now <Zap className="ml-2 h-5 w-5" />
                </motion.button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <Footer />
      </div>
      );
};

export default LandingPage;